import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, from, Observable, throwError } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';
import {
  fetchAuthSession,
  getCurrentUser,
  signIn,
  signOut,
  confirmSignIn,
} from '@aws-amplify/auth';
import { Router } from '@angular/router';
import { PermissionsService } from './permissions.service';

export interface AuthCredentials {
  username: string;
  password: string;
}

export interface AuthState {
  state: string;
  user: any;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private permissionsService = inject(PermissionsService);
  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  isAuthenticated$ = this.isAuthenticatedSubject.asObservable();
  private _redirectUrl: string | null = null;

  private isLoggedIn = new BehaviorSubject<boolean>(false);
  private authState = new BehaviorSubject<AuthState>({ state: '', user: null });
  private router = inject(Router);

  constructor() {
    this.initializeAuthState();
  }

  private async initializeAuthState() {
    const isLogged = sessionStorage.getItem('IsLogged') === 'true';

    if (isLogged) {
      this.isAuthenticatedSubject.next(true);
      this.isLoggedIn.next(true);
    }

    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      if (user && session.tokens) {
        this.isAuthenticatedSubject.next(true);
        this.isLoggedIn.next(true);
        this.setAuthState({ state: 'signedIn', user: session });

        // Load permissions and handle any errors
        await this.loadPermissions().toPromise();
      } else {
        this.handleUnauthenticatedState();
      }
    } catch (err) {
      console.error('Auth state check failed:', err);
      this.handleUnauthenticatedState();
    }
  }

  private loadPermissions(): Observable<string[]> {
    return this.permissionsService.loadUserPermissions().pipe(
      catchError((error) => {
        console.error('Failed to load permissions:', error);
        this.handleUnauthenticatedState();
        return throwError(() => error);
      })
    );
  }

  private handleUnauthenticatedState() {
    this.isLoggedIn.next(false);
    this.isAuthenticatedSubject.next(false);
    this.setAuthState({ state: 'signedOut', user: null });
    this.permissionsService.clearPermissions();
    sessionStorage.clear();
  }

  set redirectUrl(url: string) {
    this._redirectUrl = url;
  }

  get redirectUrl(): string {
    return this._redirectUrl || '/';
  }

  async checkAuthState(): Promise<boolean> {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();

      if (user && session.tokens) {
        await this.loadPermissions().toPromise();
        this.isLoggedIn.next(true);
        this.isAuthenticatedSubject.next(true);
        return true;
      }

      this.handleUnauthenticatedState();
      return false;
    } catch (err) {
      this.handleUnauthenticatedState();
      return false;
    }
  }

  get isLoggedIn$() {
    return this.isLoggedIn.asObservable();
  }

  get getAuthState$() {
    return this.authState.asObservable();
  }

  setAuthState(authState: AuthState) {
    this.authState.next(authState);
  }

  async signIn(username: string, password: string) {
    try {
      try {
        const currentSession = await fetchAuthSession();
        if (currentSession) {
          await signOut();
          await this.Logout();
        }
      } catch (sessionError) {
        console.log('No existing session found');
      }

      const signInResult = await signIn({ username, password });

      if (
        signInResult.nextStep?.signInStep ===
        'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED'
      ) {
        return {
          challengeName: 'NEW_PASSWORD_REQUIRED',
          code: 100,
          message: 'New password required',
        };
      }

      const sessionData = await fetchAuthSession();
      let userGroups = sessionData.tokens?.idToken?.payload?.[
        'cognito:groups'
      ] as string[] | undefined;

      if (Array.isArray(userGroups)) {
        // Transform each group to proper case and join with '/'
        const formattedGroups = userGroups
          .map(
            (group) =>
              group.charAt(0).toUpperCase() + group.slice(1).toLowerCase()
          )
          .join('/');

        console.log('User groups:', formattedGroups); // Will output: "Admin/Accountant/Manager/etc"
        sessionStorage.setItem('userGroups', formattedGroups);
      } else {
        sessionStorage.setItem('userGroups', '');
      }

      const isAdmin = Array.isArray(userGroups) && userGroups.includes('admin');

      // Set auth state and session data
      this.isAuthenticatedSubject.next(true);
      this.setAuthStatus('signedIn');
      this.setAuthState({ state: 'signedIn', user: sessionData });

      // Store session data
      sessionStorage.setItem(
        'token',
        sessionData.tokens?.accessToken.toString() || ''
      );
      sessionStorage.setItem('IsLogged', 'true');
      sessionStorage.setItem(
        'mobile_no',
        sessionData.tokens?.idToken?.payload?.['phone_number']?.toString() || ''
      );
      sessionStorage.setItem(
        'given_name',
        sessionData.tokens?.idToken?.payload?.['given_name']?.toString() || ''
      );
      sessionStorage.setItem('isAdmin', isAdmin.toString());

      await this.loadPermissions().toPromise();

      return {
        success: true,
        data: sessionData,
        message: 'Successfully signed in',
      };
    } catch (err: any) {
      console.error('Sign in error:', err);
      this.handleUnauthenticatedState();

      if (err.name === 'NotAuthorizedException') {
        return {
          success: false,
          code: 401,
          message: 'Invalid username or password',
        };
      }

      return {
        success: false,
        data: err,
        code: 500,
        message: err.message,
      };
    }
  }

  async Logout() {
    try {
      await signOut();
      this.handleUnauthenticatedState();

      this.router.navigate(['/auth/login']);
    } catch (error) {
      console.error('Error signing out: ', error);
      // Still clear everything locally even if AWS signout fails
      this.handleUnauthenticatedState();
      this.router.navigate(['/auth/login']);
    }
  }

  setAuthStatus(state: string) {
    switch (state) {
      case 'signedIn':
        this.isLoggedIn.next(true);
        break;
      case 'signedOut':
        this.isLoggedIn.next(false);
        break;
      default:
        this.isLoggedIn.next(false);
        break;
    }
  }

  async completeNewPasswordChallenge(
    username: string,
    newPassword: string
  ): Promise<any> {
    try {
      const challengeResponse = await confirmSignIn({
        challengeResponse: newPassword,
      });

      if (challengeResponse.isSignedIn) {
        const sessionData = await fetchAuthSession();
        const userGroups =
          sessionData.tokens?.idToken?.payload?.['cognito:groups'] || [];
        const isAdmin =
          Array.isArray(userGroups) && userGroups.includes('admin');

        this.isAuthenticatedSubject.next(true);
        this.setAuthStatus('signedIn');
        this.setAuthState({ state: 'signedIn', user: sessionData });

        sessionStorage.setItem(
          'token',
          sessionData.tokens?.accessToken.toString() || ''
        );
        sessionStorage.setItem('IsLogged', 'true');
        sessionStorage.setItem(
          'mobile_no',
          sessionData.tokens?.idToken?.payload?.['phone_number']?.toString() ||
            ''
        );
        sessionStorage.setItem('isAdmin', isAdmin.toString());

        // Load permissions after successful password change
        await this.loadPermissions().toPromise();

        return {
          success: true,
          data: isAdmin,
          code: 200,
          message: 'Password changed successfully',
        };
      }

      return {
        success: false,
        code: 400,
        message: 'Failed to complete password challenge',
      };
    } catch (error: any) {
      console.error('Password challenge error:', error);

      if (error.name === 'InvalidPasswordException') {
        return {
          success: false,
          code: 400,
          message: 'Password does not meet requirements',
        };
      }

      if (error.name === 'NotAuthorizedException') {
        return {
          success: false,
          code: 401,
          message: 'The current session is invalid. Please sign in again.',
        };
      }

      return {
        success: false,
        data: error,
        code: 500,
        message: error.message || 'Failed to change password',
      };
    }
  }

  async refreshSession(): Promise<boolean> {
    try {
      const session = await fetchAuthSession();
      
      if (session && session.tokens) {
        sessionStorage.setItem('token', session.tokens.accessToken.toString());
        this.isAuthenticatedSubject.next(true);
        this.setAuthState({ state: 'signedIn', user: session });
        await this.loadPermissions().toPromise();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Failed to refresh session:', error);
      this.handleUnauthenticatedState();
      return false;
    }
  }
}
